/**
 * 安全工具模块 - 提供客户端安全功能
 */

// 生成设备指纹
export function generateDeviceFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset(),
    screen.width,
    screen.height,
    screen.colorDepth,
    navigator.hardwareConcurrency || 0,
    navigator.platform,
  ];
  
  const fingerprint = components.join("|");
  return hashString(fingerprint);
}

// 简单哈希函数
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

// 登录尝试限制
const LOGIN_ATTEMPTS_KEY = "login_attempts";
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15分钟

interface LoginAttempts {
  count: number;
  lastAttempt: number;
  lockedUntil?: number;
}

export function getLoginAttempts(): LoginAttempts {
  try {
    const stored = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // 忽略解析错误
  }
  return { count: 0, lastAttempt: 0 };
}

export function recordFailedLogin(): { locked: boolean; remainingTime?: number } {
  const attempts = getLoginAttempts();
  const now = Date.now();
  
  // 检查是否已解锁
  if (attempts.lockedUntil && now > attempts.lockedUntil) {
    // 锁定期已过，重置
    attempts.count = 0;
    attempts.lockedUntil = undefined;
  }
  
  attempts.count += 1;
  attempts.lastAttempt = now;
  
  // 超过最大尝试次数，锁定账户
  if (attempts.count >= MAX_ATTEMPTS) {
    attempts.lockedUntil = now + LOCKOUT_DURATION;
    localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));
    return { 
      locked: true, 
      remainingTime: Math.ceil(LOCKOUT_DURATION / 60000) 
    };
  }
  
  localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(attempts));
  return { locked: false };
}

export function resetLoginAttempts(): void {
  localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
}

export function isAccountLocked(): { locked: boolean; remainingTime?: number } {
  const attempts = getLoginAttempts();
  const now = Date.now();
  
  if (attempts.lockedUntil && now < attempts.lockedUntil) {
    const remaining = Math.ceil((attempts.lockedUntil - now) / 60000);
    return { locked: true, remainingTime: remaining };
  }
  
  return { locked: false };
}

export function getRemainingAttempts(): number {
  const attempts = getLoginAttempts();
  return Math.max(0, MAX_ATTEMPTS - attempts.count);
}

// 防止自动化攻击的延迟
export function getLoginDelay(): number {
  const attempts = getLoginAttempts();
  if (attempts.count === 0) return 0;
  // 指数退避：每次失败增加延迟
  return Math.min(attempts.count * 1000, 5000);
}

// 输入验证
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.length <= 255;
}

export function validatePassword(password: string): { valid: boolean; message?: string } {
  if (password.length < 8) {
    return { valid: false, message: "密码长度至少8位" };
  }
  if (password.length > 128) {
    return { valid: false, message: "密码长度不能超过128位" };
  }
  if (!/[A-Za-z]/.test(password)) {
    return { valid: false, message: "密码需包含字母" };
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: "密码需包含数字" };
  }
  return { valid: true };
}

// 安全的会话检查
export function isSessionExpiringSoon(expiresAt?: number): boolean {
  if (!expiresAt) return false;
  const fiveMinutes = 5 * 60 * 1000;
  return Date.now() > (expiresAt * 1000 - fiveMinutes);
}
